import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getBoundingBox, getDrawingBox, getSVGBoundingBox } from "reduxModules/ducks/ui";
import { isInside } from "utilities/geometric";

/**
 * Interface for mouse position coordinates
 */
interface MousePosition {
  x: number;
  y: number;
}

/**
 * Interface for SVG bounding box
 */
interface SVGBoundingBox {
  top: number;
  left: number;
}

/**
 * Interface for drawing box
 */
interface DrawingBox {
  bbox: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    cx: number;
    cy: number;
    width: number;
    height: number;
  };
  scale: number;
}

/**
 * Custom hook to track mouse position within SVG container
 * 
 * @param ref - Whether to return a ref to the mouse position
 * @param all - Whether to allow mouse position outside the perimeter
 * @returns Mouse position coordinates or null if outside bounds (or ref to these)
 */
const useMousePosition = (ref = false, all = true): MousePosition | null => {
  // Get bounding box and scale from Redux
  const { bbox, scale } = useSelector((state: any) => getDrawingBox(state)) as DrawingBox;
  const { top, left } = useSelector((state: any) => getSVGBoundingBox(state)) as SVGBoundingBox;
  
  // State to track mouse position
  const [mousePos, setMousePos] = useState<MousePosition | null>(null);
  
  // Ref to track mouse position
  const mouseRef = useRef<MousePosition | null>({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      // Calculate mouse position relative to SVG container
      const x = event.clientX - left;
      const y = event.clientY - top;
      
      // Check if mouse is inside bounding box or if all positions are allowed
      const isIn = isInside([x, y], bbox) || all;
      
      // Set state and ref based on position check
      setMousePos(isIn ? { x, y } : null);
      mouseRef.current = isIn ? { x, y } : null;
    };
    
    // Add event listener
    window.addEventListener('mousemove', handleMouseMove);
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [bbox, all, left, top]); // Add dependencies to useEffect

  // Return either ref or state based on ref parameter
  return ref ? mouseRef.current : mousePos;
};

export default useMousePosition;
